import { useToast } from '@chakra-ui/react';
import {
  GetBuyerOfferAnalysisDocument,
  useGetBoaShareLinksQuery,
  useRevokeBoaShareLinkMutation,
  useSendBoaShareLinkMutation,
} from '@client/graphql/__generated__/main-operations';
import { useState } from 'react';
import { useLocation } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { openInNewTab } from '~/apps/consumer/utils/browser.utils';
import { ShareModal } from '../../ShareModal/ShareModal';
import { useBuyerOfferAnalysisContext } from '../Context/BuyerOfferAnalysisContext';

export const ShareWithBuyerModal = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const {
    isShareWithBuyerModalOpen,
    onShareWithBuyerModalClose,
    title,
    buyerOfferAnalysis,
  } = useBuyerOfferAnalysisContext();
  const [email, setEmail] = useState(
    buyerOfferAnalysis?.party?.primaryConsumer?.email || ''
  );
  const [phoneNumber, setPhoneNumber] = useState(
    buyerOfferAnalysis?.party?.primaryConsumer?.phoneNumber || ''
  );
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const [sendBoaShareLink] = useSendBoaShareLinkMutation();
  const [revokeBOAShareLink] = useRevokeBoaShareLinkMutation();
  const { data: shareLinksData } = useGetBoaShareLinksQuery({
    variables: {
      buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
    },
    skip: !buyerOfferAnalysis?.id,
  });
  const shareLinks = shareLinksData?.getBOAShareLinks || [];

  const revokeShareLink = (token: string) => {
    void revokeBOAShareLink({
      variables: {
        input: {
          token,
          buyerOfferAnalysisId: buyerOfferAnalysis?.id || '',
        },
      },
      refetchQueries: [
        {
          query: GetBuyerOfferAnalysisDocument,
          variables: {
            uuid: buyerOfferAnalysis?.id || '',
          },
        },
      ],
    });
  };

  const onShare = async () => {
    if (!buyerOfferAnalysis?.id) {
      return;
    }

    setLoading(true);

    await sendBoaShareLink({
      variables: {
        input: {
          buyerOfferAnalysisId: buyerOfferAnalysis?.id,
          recipientEmail: email,
          recipientPhoneNumber: phoneNumber,
          message,
        },
      },
      refetchQueries: [
        {
          query: GetBuyerOfferAnalysisDocument,
          variables: { uuid: buyerOfferAnalysis?.id || '' },
        },
      ],
      onCompleted: () => {
        toast({
          title: 'Offer Scorecard shared',
          description: 'The Offer Scorecard has been shared with the buyer',
          status: 'success',
        });
      },
      onError: () => {
        toast({
          title: 'Error sharing Offer Scorecard',
          description: 'There was an error sharing the Offer Scorecard',
          status: 'error',
        });
      },
    });
    setLoading(false);
    onShareWithBuyerModalClose();
  };

  const onPreview = () => {
    // Preserve any existing query parameters
    searchParams.set('isPreview', 'true');
    const url = `${location.pathname}?${searchParams.toString()}`;
    openInNewTab(url);
  };

  return (
    <ShareModal
      email={email}
      isOpen={isShareWithBuyerModalOpen}
      loading={loading}
      message={message}
      phoneNumber={phoneNumber}
      revokeShareLink={revokeShareLink}
      setEmail={setEmail}
      setMessage={setMessage}
      setPhoneNumber={setPhoneNumber}
      shareLinks={shareLinks}
      title={title || 'Offer Scorecard'}
      onClose={onShareWithBuyerModalClose}
      onPreview={onPreview}
      onShare={onShare}
    />
  );
};
